import React, { useEffect } from 'react'

import { useMicPermission } from '../hooks/useMicPermission'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { HiExclamation } from 'react-icons/hi'

import { StaticBanner } from '@/features/product-messages'
import { VIEW, trackEvent } from '@/utils/analytics'

/**
 * Analytics name for tracking mic permission banner events
 */
const ANALYTIC_NAME = 'Mic Permission Banner' as const

/**
 * Message displayed when microphone permissions are not granted
 */
const MIC_PERMISSION_MESSAGE =
  'Microphone access needed for emergency calls' as const

/**
 * MicPermissionBanner component displays a persistent banner when
 * microphone permissions are not granted and emergency calling is enabled.
 *
 * @returns A banner component or null if conditions are not met
 */
export const MicPermissionBanner: React.FC = () => {
  const isEmergencyCallEnabled = useFeatureFlag(FeatureFlag.emergencyCall)
  const { hasPermission, isChecking } = useMicPermission()

  const shouldShowBanner =
    isEmergencyCallEnabled && !isChecking && hasPermission === false

  // Track when the banner is shown to the user
  useEffect(() => {
    if (shouldShowBanner) {
      trackEvent(`${VIEW} ${ANALYTIC_NAME}`)
    }
  }, [shouldShowBanner])

  if (!shouldShowBanner) {
    return null
  }

  return (
    <StaticBanner
      dismissible={false}
      icon={<HiExclamation />}
      message={MIC_PERMISSION_MESSAGE}
      status='error'
    />
  )
}
